import { createContext, useContext, useEffect, useState } from "react";

export const THEMES = {
  ocean: {
    name: "Ocean & Forest",
    base: { primary: "#2F6F8F", c2: "#4E8A6E", c3: "#3BA3A0", c4: "#2F4A55", c5: "#4F86B0", bg: "#F4F8F7", surface: "#E6EFEC", ink: "#1B2B33", muted: "#50646B" },
    hero: "https://images.unsplash.com/photo-1468581264429-2548ef9eb732?crop=entropy&cs=srgb&fm=jpg&q=75&w=1600",
    side: "https://images.unsplash.com/photo-1689414126346-b21dfbc308de?crop=entropy&cs=srgb&fm=jpg&q=70&w=600",
  },
  hearth: {
    name: "Warm Hearth",
    base: { primary: "#C86D51", c2: "#7A8B7B", c3: "#D99B26", c4: "#5B4636", c5: "#8E84A8", bg: "#FBF8F3", surface: "#F3EDE2", ink: "#2C221E", muted: "#706155" },
    hero: "https://images.unsplash.com/photo-1775129667571-7fa3f51c05e3?crop=entropy&cs=srgb&fm=jpg&q=75&w=1600",
    side: "https://images.unsplash.com/photo-1599008634438-fc6d2be54835?crop=entropy&cs=srgb&fm=jpg&q=70&w=600",
  },
  meadow: {
    name: "Sage Meadow",
    base: { primary: "#4F7A4A", c2: "#8BA35B", c3: "#C9A646", c4: "#2F3B2A", c5: "#5C8A8A", bg: "#F6F8F2", surface: "#EAEFE2", ink: "#1F261C", muted: "#5C6655" },
    hero: "https://images.unsplash.com/photo-1523575708161-ad0fc2a9b951?crop=entropy&cs=srgb&fm=jpg&q=75&w=1600",
    side: "https://images.unsplash.com/photo-1689414126346-b21dfbc308de?crop=entropy&cs=srgb&fm=jpg&q=70&w=600",
  },
  lavender: {
    name: "Lavender Fields",
    base: { primary: "#7A5FA8", c2: "#6F9A7E", c3: "#C27BA0", c4: "#3E3558", c5: "#6C8FC7", bg: "#F8F6FB", surface: "#EEEAF5", ink: "#231E30", muted: "#625A72" },
    hero: "https://images.unsplash.com/photo-1474540412665-1cdae210ae6b?crop=entropy&cs=srgb&fm=jpg&q=75&w=1600",
    side: "https://images.unsplash.com/photo-1474540412665-1cdae210ae6b?crop=entropy&cs=srgb&fm=jpg&q=70&w=600",
  },
  rose: {
    name: "Rose Garden",
    base: { primary: "#B8566F", c2: "#6E9277", c3: "#D08A5B", c4: "#4A2F3A", c5: "#8A7BB0", bg: "#FCF6F6", surface: "#F5E9EA", ink: "#2B1D21", muted: "#6E5A5F" },
    hero: "https://images.unsplash.com/photo-1599008634438-fc6d2be54835?crop=entropy&cs=srgb&fm=jpg&q=75&w=1600",
    side: "https://images.unsplash.com/photo-1622763846204-5d0bf5031e06?crop=entropy&cs=srgb&fm=jpg&q=70&w=600",
  },
  candlelight: {
    name: "Candlelight",
    dark: true,
    base: { primary: "#D97B5F", c2: "#8CA08D", c3: "#E0A93E", c4: "#D9C9B4", c5: "#A89BC4", bg: "#1C1816", surface: "#25201D", card: "#2E2724", ink: "#F3EDE2", muted: "#A89A8E" },
    hero: "https://images.unsplash.com/photo-1775129667571-7fa3f51c05e3?crop=entropy&cs=srgb&fm=jpg&q=75&w=1600",
    side: "https://images.unsplash.com/photo-1599008634438-fc6d2be54835?crop=entropy&cs=srgb&fm=jpg&q=70&w=600",
  },
  midnight: {
    name: "Midnight Lagoon",
    dark: true,
    base: { primary: "#3F8DB3", c2: "#5FAE85", c3: "#45B3AD", c4: "#B9CDD4", c5: "#7FA8D9", bg: "#111A1F", surface: "#18242A", card: "#1E2C33", ink: "#E6EEF0", muted: "#9AAEB4" },
    hero: "https://images.unsplash.com/photo-1616159988985-750036b28b40?crop=entropy&cs=srgb&fm=jpg&q=75&w=1600",
    side: "https://images.unsplash.com/photo-1689414126346-b21dfbc308de?crop=entropy&cs=srgb&fm=jpg&q=70&w=600",
  },
};

const rgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const mix = (a, b, t) => {
  const [x, y] = [rgb(a), rgb(b)];
  return x.map((v, i) => Math.round(v + (y[i] - v) * t));
};
const W = "#FFFFFF";
const K = "#000000";

const tokens = ({ primary, c2, c3, c4, c5, bg, surface, ink, muted, card = W }, dark) => {
  const soft = dark ? card : W;
  const deep = dark ? W : K;
  const t = {
    primary: rgb(primary), "primary-hover": mix(primary, K, 0.18), "primary-soft": mix(primary, soft, dark ? 0.8 : 0.88),
    "primary-line": mix(primary, soft, dark ? 0.55 : 0.7), "primary-ink": mix(primary, deep, 0.3), "primary-light": mix(primary, W, 0.55),
    c4: rgb(c4), bg: rgb(bg), surface: rgb(surface), ink: rgb(ink), muted: rgb(muted), card: rgb(card),
    "surface-hover": mix(surface, ink, 0.07), line: mix(surface, ink, dark ? 0.12 : 0.08), "line-strong": mix(surface, ink, 0.2),
    faint: mix(muted, bg, 0.35), "note-plain": mix(bg, card, dark ? 0.6 : 0.4), danger: rgb(dark ? "#E58A75" : "#A4492F"),
  };
  Object.entries({ c2, c3, c5 }).forEach(([k, v]) => {
    t[k] = rgb(v);
    t[`${k}-soft`] = mix(v, soft, dark ? 0.8 : 0.87);
    t[`${k}-line`] = mix(v, soft, dark ? 0.55 : 0.7);
    t[`${k}-ink`] = mix(v, deep, 0.3);
  });
  return t;
};

export const applyTheme = (id) => {
  const theme = THEMES[id] || THEMES.ocean;
  const root = document.documentElement.style;
  Object.entries(tokens(theme.base, theme.dark)).forEach(([k, v]) => root.setProperty(`--t-${k}`, v.join(" ")));
  root.colorScheme = theme.dark ? "dark" : "light";
};

const ThemeCtx = createContext(null);
const KEY = "bb-theme";

export const ThemeProvider = ({ children }) => {
  const [themeId, setThemeId] = useState(() => (THEMES[localStorage.getItem(KEY)] ? localStorage.getItem(KEY) : "ocean"));
  useEffect(() => {
    applyTheme(themeId);
    localStorage.setItem(KEY, themeId);
  }, [themeId]);
  return <ThemeCtx.Provider value={{ themeId, theme: THEMES[themeId], setThemeId }}>{children}</ThemeCtx.Provider>;
};

export const useTheme = () => useContext(ThemeCtx);
